import * as RadixDialog from '@radix-ui/react-dialog'
import { type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from './cn'
import { Button } from './Button'

/**
 * A centred modal.
 *
 * Drawer is pinned to the right edge at full height, so it can't serve as a
 * lightbox or a confirmation — this is the generic counterpart.
 */
const SIZES = {
  sm: 'w-[min(24rem,calc(100vw-2rem))]',
  md: 'w-[min(36rem,calc(100vw-2rem))]',
  lg: 'w-[min(52rem,calc(100vw-2rem))]',
  full: 'w-[calc(100vw-3rem)] h-[calc(100vh-3rem)]',
} as const

export function Dialog({
  open,
  onOpenChange,
  title,
  children,
  size = 'md',
  bare = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  children: ReactNode
  size?: keyof typeof SIZES
  /** Skips the padding and header chrome — for an image filling the frame. */
  bare?: boolean
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[1px]" />
        <RadixDialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'rounded-xl border border-border bg-bg-elevated shadow-2xl focus:outline-none',
            'flex flex-col overflow-hidden',
            SIZES[size],
          )}
        >
          {/* Radix requires a title for screen readers even when it isn't drawn. */}
          <RadixDialog.Title className={cn(bare && 'sr-only', !bare && 'px-4 pt-4 text-sm font-semibold text-fg')}>
            {title ?? 'Dialog'}
          </RadixDialog.Title>
          <RadixDialog.Close
            className="absolute right-3 top-3 z-10 rounded-md p-1 text-fg-subtle hover:bg-bg-muted hover:text-fg"
            aria-label="Close"
          >
            <X size={16} />
          </RadixDialog.Close>
          <div className={cn('min-h-0 flex-1', !bare && 'p-4')}>{children}</div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmLabel?: string
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} size="sm">
      {description && <p className="mb-4 text-sm text-fg-subtle break-words">{description}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => {
            onConfirm()
            onOpenChange(false)
          }}
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  )
}
